import React, { useEffect, useState } from 'react';
import { Linking, Modal, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  initialWindowMetrics,
  SafeAreaProvider,
  SafeAreaView
} from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import Button from '../../components/Button';
import { H1, P } from '../../components/Typography';
import Colors from '../../constants/Colors';
import { useLocationContext } from '../../context/LocationContext';
import { logAnalyticsEvent } from '../../common/analytics';
import { GpsPermissionLevel } from '../../tools/location-sync';

const StyledContainer = styled(SafeAreaView)`
  flex: 1;
  background-color: ${Colors.white};
`;

const StyledContent = styled.ScrollView`
  flex: 1;
  padding: 24px;
`;

const StyledFooter = styled.View`
  padding: 24px;
  gap: 12px;
`;

const StyledParagraph = styled(P)`
  margin-top: 12px;
`;

type Phase = 'intro' | 'escalate' | 'blocked' | 'degraded' | 'active';

const ALWAYS_PROMPT_SPENT_KEY = 'gpsAlwaysPromptSpent';

/**
 * The phase the screen must OPEN in depends on the current authorization,
 * because the OS will not show the same dialogs from every starting state
 * (CD-284). The old screen assumed notDetermined for everyone and promised
 * "on the next screens choose Allow While Using…" — for a participant whose
 * status was already determined, no dialog ever appeared, the button sat on
 * "Requesting…", and the flow ended with no feedback at all.
 *
 * - 'unknown' (never asked): the full priming pitch; the dialogs will come.
 * - whenInUse: iOS can still present the one-time "Change to Always Allow"
 *   upgrade dialog, so an in-app request is worth attempting.
 * - denied/restricted on iOS: the OS will NEVER re-prompt. The only honest
 *   control is a Settings deep link — a button that appears to do nothing is
 *   the worst possible response. Android can re-prompt after a denial, so it
 *   keeps the request path alongside Settings.
 */
const initialPhase = (level: GpsPermissionLevel): Phase => {
  if (level === 'denied' || level === 'restricted') return 'blocked';
  if (level === 'whenInUse') return 'escalate';
  return 'intro';
};

/**
 * Location permission priming (CD-256, PRD Flow 1). The only
 * participant-facing GPS surface; also serves as the App Store reviewer
 * demonstration of background-location justification. Copy never says
 * "tracking": it explains purpose, what is collected, who sees it, and how
 * to withdraw.
 */
export const LocationPrimingScreen: React.FC<{ onDone: () => void }> = ({
  onDone
}) => {
  const { permissionLevel, requestPermission } = useLocationContext();
  const [phase, setPhase] = useState<Phase>(() =>
    initialPhase(permissionLevel)
  );
  const [requesting, setRequesting] = useState(false);

  /** Permission level the OS request was last made from; see settingsOffered. */
  const [requestedFrom, setRequestedFrom] = useState<GpsPermissionLevel>();

  const [alwaysPromptSpent, setAlwaysPromptSpent] = useState<boolean>();

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      setAlwaysPromptSpent(false);
      return;
    }
    AsyncStorage.getItem(ALWAYS_PROMPT_SPENT_KEY)
      .then(value => setAlwaysPromptSpent(value === 'true'))
      .catch(() => setAlwaysPromptSpent(false));
  }, []);

  const phaseFor = (level: GpsPermissionLevel): Phase =>
    level === 'whenInUse' && alwaysPromptSpent
      ? 'degraded'
      : initialPhase(level);

  // A level change that arrives from OUTSIDE the request call — the
  // participant flipping the setting during a Settings round-trip — must move
  // the phase with it, in EVERY direction. Following only the grant left a
  // participant who went from denied to "While Using" staring at blocked's
  // "access is off" copy while the escalation dialog was actually available.
  // 'unknown' is ignored: it is either the never-asked intro (already the
  // mount phase) or a transient provider error, and neither should yank a
  // participant out of the phase they are reading.
  useEffect(() => {
    if (permissionLevel === 'always') setPhase('active');
    else if (permissionLevel !== 'unknown') setPhase(phaseFor(permissionLevel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionLevel, alwaysPromptSpent]);

  const enableLocation = async () => {
    const from = permissionLevel;
    setRequesting(true);
    setRequestedFrom(from);
    try {
      const level = await requestPermission();
      if (from === 'whenInUse' && level === 'whenInUse') {
        if (Platform.OS === 'ios') {
          setAlwaysPromptSpent(true);
          AsyncStorage.setItem(ALWAYS_PROMPT_SPENT_KEY, 'true').catch(
            () => undefined
          );
        }
        setPhase('degraded');
        return;
      }
      // Land on the phase that matches what the OS actually said: whenInUse
      // continues to the Always escalation, denied/restricted to the Settings
      // route (keeping Android's re-request button). Blanket-routing every
      // non-grant to degraded bypassed the escalation step entirely.
      setPhase(
        level === 'always'
          ? 'active'
          : level === 'unknown'
            ? 'degraded'
            : initialPhase(level)
      );
    } catch (error) {
      // Every path out of "Requesting…" must end in a visible state; degraded
      // is reserved for exactly this — the request itself failed, so the
      // Settings route is the only useful next step.
      setPhase('degraded');
    } finally {
      setRequesting(false);
    }
  };

  const openSettings = () => {
    logAnalyticsEvent('gps_settings_opened', {});
    Linking.openSettings();
  };

  const settingsOffered = !!requestedFrom;

  if (phase === 'escalate' && alwaysPromptSpent === undefined) return null;

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics ?? undefined}>
      <StyledContainer edges={['top', 'bottom', 'left', 'right']}>
        {phase === 'intro' && (
          <>
            <StyledContent>
              <H1>Share your location with your study</H1>
              <StyledParagraph>
                {`Your study measures time spent at home. To do that, Chloe collects your phone's location in the background, so participating takes no daily effort.`}
              </StyledParagraph>
              <StyledParagraph>
                Your study team only sees a daily summary of time spent at home,
                never a map of where you go. Your exact location is protected
                health information and stays private.
              </StyledParagraph>
              <StyledParagraph>
                {Platform.OS === 'ios'
                  ? `On the next screen, choose "Allow While Using App". Later, when iOS asks, choose "Change to Always Allow".`
                  : `On the next screens, allow location access and choose "Allow all the time".`}
              </StyledParagraph>
              {Platform.OS === 'ios' && (
                <StyledParagraph>
                  {`iOS may also ask about "Motion & Fitness" — allowing it helps Chloe collect location using less battery.`}
                </StyledParagraph>
              )}
              <StyledParagraph>
                Location sharing is designed to be battery-friendly. You can
                withdraw at any time by contacting your study team or turning
                off location access in Settings.
              </StyledParagraph>
            </StyledContent>
            <StyledFooter>
              <Button disabled={requesting} onPress={enableLocation}>
                {requesting ? 'Requesting…' : 'Continue'}
              </Button>
            </StyledFooter>
          </>
        )}
        {phase === 'escalate' && (
          <>
            <StyledContent>
              <H1>One more step</H1>
              <StyledParagraph>
                {Platform.OS === 'ios'
                  ? `You've allowed location while using the app. Measuring time at home needs location set to "Always", so it works while the app is closed.`
                  : `You've allowed location while using the app. Measuring time at home needs location set to "Allow all the time", so it works while the app is closed.`}
              </StyledParagraph>
            </StyledContent>
            <StyledFooter>
              <Button disabled={requesting} onPress={enableLocation}>
                {requesting ? 'Requesting…' : 'Continue'}
              </Button>
            </StyledFooter>
          </>
        )}
        {phase === 'blocked' && (
          <>
            <StyledContent>
              <H1>Location access is turned off</H1>
              <StyledParagraph>
                {`Location access for Chloe is turned off, so your study can't measure time at home right now and your study team will see gaps.`}
              </StyledParagraph>
              {settingsOffered && (
                <StyledParagraph>
                  {Platform.OS === 'ios'
                    ? `iOS won't ask again on its own — open Settings and set Location to "Always".`
                    : `Open Settings and set Location permission to "Allow all the time".`}
                </StyledParagraph>
              )}
              {permissionLevel === 'restricted' && (
                <StyledParagraph>
                  {`Access may be limited by a device restriction (such as parental controls or a work profile). If Settings doesn't let you change it, contact your study team.`}
                </StyledParagraph>
              )}
            </StyledContent>
            <StyledFooter>
              {(Platform.OS !== 'ios' || !settingsOffered) && (
                <Button disabled={requesting} onPress={enableLocation}>
                  {requesting ? 'Requesting…' : 'Enable location sharing'}
                </Button>
              )}
              {settingsOffered && (
                <Button onPress={openSettings}>Open Settings</Button>
              )}
              <Button outlined onPress={onDone}>
                Not now
              </Button>
            </StyledFooter>
          </>
        )}
        {phase === 'degraded' && (
          <>
            <StyledContent>
              <H1>Location access is limited</H1>
              <StyledParagraph>
                {`Chloe doesn't have background location access, so your study can't measure time at home reliably. Your study team will see gaps in your data and may reach out.`}
              </StyledParagraph>
              <StyledParagraph>
                {Platform.OS === 'ios'
                  ? `To fix this, open Settings and set Location to "Always".`
                  : `To fix this, open Settings and set Location permission to "Allow all the time".`}
              </StyledParagraph>
            </StyledContent>
            <StyledFooter>
              <Button onPress={openSettings}>Open Settings</Button>
              <Button outlined onPress={onDone}>
                Not now
              </Button>
            </StyledFooter>
          </>
        )}
        {phase === 'active' && (
          <>
            <StyledContent>
              <H1>Location sharing active</H1>
              <StyledParagraph>
                {`You're all set. Chloe collects location in the background for your study, and only a daily summary of time at home is shared with your study team.`}
              </StyledParagraph>
            </StyledContent>
            <StyledFooter>
              <Button onPress={onDone}>Done</Button>
            </StyledFooter>
          </>
        )}
      </StyledContainer>
    </SafeAreaProvider>
  );
};

/**
 * Shows the priming flow once per app session when the server enables
 * capture but "always" permission hasn't been granted yet. Covers both new
 * enrollments (post-consent) and already-enrolled participants when the org
 * flag turns on.
 */
export const LocationPermissionGate: React.FC = () => {
  const { enabled, permissionLevel } = useLocationContext();
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);

  // 'denied'/'restricted' are included deliberately: the screen's blocked
  // phase is the ONLY in-app route back to working capture (it deep-links to
  // Settings). Excluding them stranded exactly the participants who need
  // recovery. Still at most once per app session via `dismissed`.
  const shouldOpen =
    !dismissed && enabled === true && permissionLevel !== 'always';

  // LATCH open rather than deriving visibility per render: the moment a grant
  // lands, permissionLevel flips to 'always' and a derived condition unmounted
  // the modal mid-flow — the participant never saw the confirmation, just a
  // screen that vanished (CD-284). Once open, only an explicit dismissal
  // closes it, so the 'active' phase gets its moment.
  useEffect(() => {
    if (shouldOpen) setOpen(true);
  }, [shouldOpen]);

  const dismiss = () => {
    setDismissed(true);
    setOpen(false);
  };

  if (!open) return null;
  return (
    <Modal animationType='slide' visible onRequestClose={dismiss}>
      <LocationPrimingScreen onDone={dismiss} />
    </Modal>
  );
};

export default LocationPrimingScreen;
